import React, { useEffect, useState } from 'react';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI } from '../utils/api';
import apiClient from '../utils/api';
import { ChevronDown, ChevronRight, Heart, Star, Quote, Calendar, BookOpen } from 'lucide-react';

const PageHeader = ({ eyebrow, title, intro }) => (
  <header className="mb-6">
    <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{eyebrow}</p>
    <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
    {intro && <p className="text-stone-600 mt-2">{intro}</p>}
  </header>
);

// ============= ÖZEL DUALAR =============
export const SpecialPrayersFull = () => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const [user, setUser] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    authAPI.getMe().then((r) => setUser(r.data)).catch(() => {});
    apiClient.get('/content/prayers').then((r) => setPrayers(r.data?.data || [])).catch(() => {});
  }, []);

  const cats = [...new Set(prayers.map((p) => p.category))];

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Özel Dualar' : 'Special Prayers'}>
      <div className="max-w-4xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Dualar' : 'Prayers'}
          title={lang === 'tr' ? 'Özel Dualar' : 'Special Prayers'}
          intro={lang === 'tr' ? 'Namaz duaları, günlük zikirler ve özel günler için seçili dualar.' : 'Prayer recitations, daily remembrance, and selected duas.'}
        />
        {cats.map((cat) => (
          <section key={cat}>
            <h2 className="text-lg font-bold text-stone-800 mb-3 capitalize" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {cat === 'namaz' ? (lang === 'tr' ? 'Namaz Duaları' : 'Prayer Duas') : (lang === 'tr' ? 'Günlük Zikir' : 'Daily Remembrance')}
            </h2>
            <div className="space-y-3">
              {prayers.filter((p) => p.category === cat).map((p) => {
                const open = openId === p.id;
                return (
                  <div key={p.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : p.id)}
                      data-testid={`prayer-${p.id}`}
                      className="w-full flex items-center justify-between p-5 hover:bg-stone-50"
                    >
                      <span className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {lang === 'en' ? p.title_en : p.title_tr}
                      </span>
                      {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {open && (
                      <div className="px-5 pb-5 space-y-3">
                        <p className="text-2xl text-right leading-loose text-stone-900" style={{ fontFamily: 'Amiri, serif', direction: 'rtl', lineHeight: 2.2 }}>
                          {p.arabic}
                        </p>
                        <p className="text-sm italic text-stone-500">{p.transliteration}</p>
                        <p className="text-base text-stone-700 leading-relaxed border-t border-stone-100 pt-3">
                          {lang === 'en' ? p.meaning_en : p.meaning_tr}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </AppShell>
  );
};

// ============= SÜNNETLER =============
export const SunnetlerFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    authAPI.getMe().then((r) => setUser(r.data)).catch(() => {});
    apiClient.get('/content/sunnahs').then((r) => setItems(r.data?.data || [])).catch(() => {});
  }, []);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Sünnetler' : 'Sunnahs'}>
      <div className="max-w-4xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Sünnet' : 'Sunnah'}
          title={lang === 'tr' ? 'Hz. Peygamber\'in (s.a.v.) Sünnetleri' : 'Prophet\'s (pbuh) Sunnahs'}
          intro={lang === 'tr' ? 'Günlük hayatta uygulayabileceğin sünnet uygulamaları.' : 'Sunnahs you can apply in daily life.'}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`sunnah-${it.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Star className={`w-5 h-5 ${isKidsMode ? 'text-amber-600' : 'text-teal-700'}`} />
                <span className="text-xs uppercase tracking-[0.2em] text-stone-500">{it.category}</span>
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'en' ? it.title_en : it.title_tr}
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                {lang === 'en' ? it.desc_en : it.desc_tr}
              </p>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
};

// ============= HADİSLER =============
export const HadithsFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    authAPI.getMe().then((r) => setUser(r.data)).catch(() => {});
    apiClient.get('/content/hadiths').then((r) => setItems(r.data?.data || [])).catch(() => {});
  }, []);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Hadisler' : 'Hadiths'}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Hadis' : 'Hadith'}
          title={lang === 'tr' ? 'Hadîs-i Şerifler' : 'Sacred Hadiths'}
          intro={lang === 'tr' ? 'Sahih kaynaklardan derlenmiş seçkin hadisler.' : 'Selected hadiths from authentic sources.'}
        />
        <div className="space-y-4">
          {items.map((h) => (
            <div key={h.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`hadith-${h.id}`}>
              <Quote className={`w-6 h-6 mb-3 ${isKidsMode ? 'text-amber-600' : 'text-teal-700'}`} />
              <p className="text-base text-stone-800 leading-relaxed italic mb-3">
                &ldquo;{lang === 'en' ? h.text_en : h.text_tr}&rdquo;
              </p>
              <p className="text-xs text-stone-500">— {h.source}</p>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
};

// ============= ÇOCUKLARA HADİSLER =============
export const KidHadithsFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    authAPI.getMe().then((r) => setUser(r.data)).catch(() => {});
    apiClient.get('/content/kid-hadiths').then((r) => setItems(r.data?.data || [])).catch(() => {});
  }, []);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Çocuklara Hadisler' : "Hadiths for Children"}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Hadis' : 'Hadith'}
          title={lang === 'tr' ? 'Sana Anlatılan Hadisler' : 'Hadiths for You'}
          intro={lang === 'tr' ? 'Hz. Peygamber Efendimiz\'in sana özel öğütleri.' : "Prophet's (pbuh) special advice for you."}
        />
        <div className="space-y-4">
          {items.map((h) => (
            <div key={h.id} className={`rounded-3xl border p-6 ${isKidsMode ? 'bg-amber-50/60 border-amber-200' : 'bg-white border-stone-200'}`} data-testid={`kidhadith-${h.id}`}>
              <Quote className="w-7 h-7 mb-3 text-amber-600" />
              <p className="text-lg text-stone-800 leading-relaxed font-medium mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                &ldquo;{lang === 'en' ? h.text_en : h.text_tr}&rdquo;
              </p>
              <p className="text-xs text-stone-500 mb-3">— {lang === 'en' ? h.source_en : h.source_tr}</p>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${isKidsMode ? 'bg-amber-200 text-amber-900' : 'bg-teal-100 text-teal-800'}`}>
                ⭐ {lang === 'en' ? h.moral_en : h.moral_tr}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
};

// ============= DİNİ GÜNLER =============
export const ReligiousDaysFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    authAPI.getMe().then((r) => setUser(r.data)).catch(() => {});
    apiClient.get('/content/religious-events').then((r) => setEvents(r.data?.data || [])).catch(() => {});
  }, []);

  const openDetail = async (ev) => {
    try {
      const r = await apiClient.get(`/content/religious-event/${ev.id}`);
      setSelected(r.data?.data);
    } catch {
      setSelected(ev);
    }
  };

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Özel Günler' : 'Religious Days'}>
      <div className="max-w-4xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Dini Takvim' : 'Religious Calendar'}
          title={lang === 'tr' ? 'Kandiller ve Bayramlar' : 'Holy Nights & Eids'}
          intro={lang === 'tr' ? 'Tıkla ve o güne özel ayet, hadis, dua öğren.' : 'Click to learn special verses, hadiths, and prayers for that day.'}
        />
        <div className="space-y-3">
          {events.map((ev) => {
            const isPast = ev.days_until !== null && ev.days_until < 0;
            const isSoon = ev.days_until !== null && ev.days_until >= 0 && ev.days_until <= 30;
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => openDetail(ev)}
                data-testid={`event-${ev.id}`}
                className={`w-full text-left rounded-2xl border p-5 flex items-center gap-4 transition-all hover:shadow-md ${isPast ? 'bg-stone-50 border-stone-200 opacity-60' : isSoon ? (isKidsMode ? 'bg-amber-50 border-amber-300' : 'bg-teal-50 border-teal-300') : 'bg-white border-stone-200'}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-800'}`}>
                  <Calendar className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {lang === 'en' ? ev.name_en : ev.name_tr}
                  </div>
                  <div className="text-xs text-stone-500 mb-1">{ev.date_2026}</div>
                  <p className="text-sm text-stone-600 leading-snug">
                    {lang === 'en' ? ev.desc_en : ev.desc_tr}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {ev.days_until !== null && ev.days_until >= 0 ? (
                    <div>
                      <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{ev.days_until}</div>
                      <div className="text-xs uppercase tracking-wider text-stone-500">{lang === 'tr' ? 'gün' : 'days'}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-stone-400 uppercase tracking-wider">{lang === 'tr' ? 'Geçmiş' : 'Past'}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="event-modal-overlay">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'en' ? selected.name_en : selected.name_tr}
              </h3>
              <button type="button" onClick={() => setSelected(null)} className="w-9 h-9 rounded-lg hover:bg-stone-100 text-stone-500 text-xl">×</button>
            </div>
            <div className="p-6 space-y-5">
              {selected.advice_tr && (
                <div className={`rounded-2xl p-4 ${isKidsMode ? 'bg-amber-50 border border-amber-200' : 'bg-teal-50 border border-teal-200'}`}>
                  <h4 className="font-semibold text-stone-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>📌 {lang === 'en' ? 'Recommended' : 'Tavsiyeler'}</h4>
                  <p className="text-sm text-stone-700">{lang === 'en' ? (selected.advice_en || selected.advice_tr) : selected.advice_tr}</p>
                </div>
              )}
              {selected.verses_tr?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-stone-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>📖 {lang === 'en' ? 'Verses' : 'Ayetler'}</h4>
                  {selected.verses_tr.map((v, i) => (
                    <p key={i} className="text-sm text-stone-700 leading-relaxed mb-2 italic">&ldquo;{v}&rdquo;</p>
                  ))}
                </div>
              )}
              {selected.hadith_tr && (
                <div>
                  <h4 className="font-semibold text-stone-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>💬 {lang === 'en' ? 'Hadith' : 'Hadis'}</h4>
                  <p className="text-sm text-stone-700 leading-relaxed italic">&ldquo;{selected.hadith_tr}&rdquo;</p>
                </div>
              )}
              {selected.duas_tr?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-stone-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>🤲 {lang === 'en' ? 'Recommended Prayers' : 'Bu Gün/Gece İçin Dualar'}</h4>
                  <ul className="space-y-1.5">
                    {selected.duas_tr.map((d, i) => (
                      <li key={i} className="text-sm text-stone-700 flex items-start gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${isKidsMode ? 'bg-amber-500' : 'bg-teal-600'}`} />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-stone-400 pt-3 border-t border-stone-100">
                {lang === 'en' ? 'Source: Diyanet İşleri Başkanlığı (Diyanet, Turkey)' : 'Kaynak: Diyanet İşleri Başkanlığı'}
              </p>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

// ============= EĞLENCELİ ALIŞTIRMALAR (Mini Game) =============
const LETTER_QUIZ = [
  { ar: 'ا', name_tr: 'Elif', name_en: 'Alif' },
  { ar: 'ب', name_tr: 'Be', name_en: 'Ba' },
  { ar: 'ت', name_tr: 'Te', name_en: 'Ta' },
  { ar: 'ث', name_tr: 'Se', name_en: 'Tha' },
  { ar: 'ج', name_tr: 'Cim', name_en: 'Jim' },
  { ar: 'ح', name_tr: 'Ha', name_en: 'Ha' },
  { ar: 'خ', name_tr: 'Hı', name_en: 'Kha' },
  { ar: 'د', name_tr: 'Dal', name_en: 'Dal' },
  { ar: 'ذ', name_tr: 'Zel', name_en: 'Dhal' },
  { ar: 'ر', name_tr: 'Ra', name_en: 'Ra' },
  { ar: 'ز', name_tr: 'Ze', name_en: 'Zay' },
  { ar: 'س', name_tr: 'Sin', name_en: 'Sin' },
  { ar: 'ش', name_tr: 'Şın', name_en: 'Shin' },
  { ar: 'ص', name_tr: 'Sad', name_en: 'Sad' },
  { ar: 'ض', name_tr: 'Dad', name_en: 'Dad' },
  { ar: 'ط', name_tr: 'Tı', name_en: 'Ta' },
  { ar: 'ظ', name_tr: 'Zı', name_en: 'Dha' },
  { ar: 'ع', name_tr: 'Ayın', name_en: 'Ayn' },
  { ar: 'غ', name_tr: 'Gayın', name_en: 'Ghayn' },
  { ar: 'ف', name_tr: 'Fe', name_en: 'Fa' },
  { ar: 'ق', name_tr: 'Kaf', name_en: 'Qaf' },
  { ar: 'ك', name_tr: 'Kef', name_en: 'Kaf' },
  { ar: 'ل', name_tr: 'Lam', name_en: 'Lam' },
  { ar: 'م', name_tr: 'Mim', name_en: 'Mim' },
  { ar: 'ن', name_tr: 'Nun', name_en: 'Nun' },
  { ar: 'و', name_tr: 'Vav', name_en: 'Waw' },
  { ar: 'ه', name_tr: 'He', name_en: 'Ha' },
  { ar: 'ي', name_tr: 'Ye', name_en: 'Ya' },
];

export const GamesExercisesFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => { authAPI.getMe().then((r) => setUser(r.data)).catch(() => {}); }, []);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Eğlenceli Alıştırmalar' : 'Fun Exercises'}>
      <div className="max-w-4xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Çocuk Modu' : 'Kids Mode'}
          title={lang === 'tr' ? 'Eğlenceli Alıştırmalar' : 'Fun Exercises'}
          intro={lang === 'tr' ? 'Öğrendiklerini oynayarak pekiştir!' : 'Reinforce what you learned by playing!'}
        />
        {!active ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setActive('quiz')}
              data-testid="game-quiz"
              className="text-left p-6 bg-white rounded-2xl border border-stone-200 hover:border-amber-400 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-3 text-2xl">
                🎯
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Harf Tahmin Oyunu' : 'Letter Guess Game'}
              </h3>
              <p className="text-sm text-stone-500">{lang === 'tr' ? 'Harfi gör, doğru ismini seç.' : 'See the letter, pick its name.'}</p>
            </button>
            <button
              type="button"
              onClick={() => setActive('audio')}
              data-testid="game-audio-quiz"
              className="text-left p-6 bg-white rounded-2xl border border-stone-200 hover:border-amber-400 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-3 text-2xl">
                🔊
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Sesli Tahmin' : 'Audio Guess'}
              </h3>
              <p className="text-sm text-stone-500">{lang === 'tr' ? 'Sesi dinle, doğru harfi seç.' : 'Listen, pick the correct letter.'}</p>
            </button>
            <button
              type="button"
              onClick={() => setActive('match')}
              data-testid="game-match"
              className="text-left p-6 bg-white rounded-2xl border border-stone-200 hover:border-amber-400 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-3 text-2xl">
                🧩
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {lang === 'tr' ? 'Harf Eşleştirme' : 'Letter Match'}
              </h3>
              <p className="text-sm text-stone-500">{lang === 'tr' ? 'Arapça harfi Türkçe okunuşu ile eşleştir.' : 'Match Arabic letter to its Turkish reading.'}</p>
            </button>
          </div>
        ) : active === 'quiz' ? (
          <LetterQuizGame onExit={() => setActive(null)} />
        ) : active === 'audio' ? (
          <AudioQuizGame onExit={() => setActive(null)} />
        ) : active === 'match' ? (
          <MatchGame onExit={() => setActive(null)} />
        ) : null}
      </div>
    </AppShell>
  );
};

const LetterQuizGame = ({ onExit }) => {
  const { lang } = useLang();
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [questions] = useState(() => {
    const shuffled = [...LETTER_QUIZ].sort(() => Math.random() - 0.5).slice(0, 15);
    return shuffled.map((correct) => {
      const wrong = LETTER_QUIZ.filter((l) => l.ar !== correct.ar).sort(() => Math.random() - 0.5).slice(0, 3);
      const opts = [correct, ...wrong].sort(() => Math.random() - 0.5);
      return { correct, opts };
    });
  });

  if (round >= questions.length) {
    return (
      <div className="bg-white rounded-3xl border border-stone-200 p-10 text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'tr' ? 'Tebrikler!' : 'Congratulations!'}
        </h2>
        <p className="text-stone-600 mb-6">
          {lang === 'tr' ? `${score} / ${questions.length} doğru cevap` : `${score} / ${questions.length} correct answers`}
        </p>
        <button
          type="button"
          onClick={onExit}
          className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
          data-testid="quiz-exit"
        >
          {lang === 'tr' ? 'Tamamla' : 'Done'}
        </button>
      </div>
    );
  }

  const q = questions[round];
  const handleAnswer = (opt) => {
    const correct = opt.ar === q.correct.ar;
    setFeedback(correct ? 'correct' : 'wrong');
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      setFeedback(null);
      setRound((r) => r + 1);
    }, 900);
  };

  return (
    <div className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
          {lang === 'tr' ? 'Soru' : 'Question'} {round + 1} / {questions.length}
        </div>
        <div className="text-xs font-semibold text-amber-700">
          {lang === 'tr' ? 'Skor' : 'Score'}: {score}
        </div>
      </div>
      <p className="text-center text-sm text-stone-600 mb-4">
        {lang === 'tr' ? 'Bu harfin adı ne?' : 'What is this letter\'s name?'}
      </p>
      <div className="text-center py-6">
        <div className="text-[120px] sm:text-[160px] text-amber-700 leading-none" style={{ fontFamily: 'Amiri, serif', lineHeight: 1 }}>
          {q.correct.ar}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        {q.opts.map((opt) => (
          <button
            key={opt.ar}
            type="button"
            onClick={() => handleAnswer(opt)}
            disabled={feedback !== null}
            data-testid={`quiz-option-${opt.ar}`}
            className={`p-4 rounded-xl border-2 font-semibold transition-all ${
              feedback && opt.ar === q.correct.ar ? 'bg-green-100 border-green-500 text-green-800' :
              feedback === 'wrong' ? 'bg-stone-100 border-stone-300 text-stone-500' :
              'bg-white border-stone-200 hover:border-amber-400 text-stone-800'
            }`}
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {lang === 'en' ? opt.name_en : opt.name_tr}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============= AUDIO QUIZ GAME =============
const AudioQuizGame = ({ onExit }) => {
  const { lang } = useLang();
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);
  const [questions] = useState(() => {
    const shuffled = [...LETTER_QUIZ].sort(() => Math.random() - 0.5).slice(0, 12);
    return shuffled.map((correct, idx) => {
      // Real Diyanet letter index = LETTER_QUIZ index + 1 (matches btn_1..btn_28 in elifba endpoint)
      const correctBtn = LETTER_QUIZ.findIndex((l) => l.ar === correct.ar) + 1;
      const wrong = LETTER_QUIZ.filter((l) => l.ar !== correct.ar).sort(() => Math.random() - 0.5).slice(0, 3);
      const opts = [correct, ...wrong].sort(() => Math.random() - 0.5);
      return { correct, opts, correctBtn };
    });
  });

  const BACKEND = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
  const playCurrent = React.useCallback(() => {
    const q = questions[round];
    if (!q) return;
    const url = `${BACKEND}/api/elifba/diyanet-audio/isimleri/${q.correctBtn}`;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const a = new Audio(url);
      audioRef.current = a;
      a.onplay = () => setPlaying(true);
      a.onended = () => setPlaying(false);
      a.onerror = () => setPlaying(false);
      a.play().catch(() => setPlaying(false));
    } catch { /* ignore */ }
  }, [questions, round, BACKEND]);

  React.useEffect(() => {
    // Auto-play on each round
    const t = setTimeout(playCurrent, 350);
    return () => clearTimeout(t);
  }, [round, playCurrent]);

  if (round >= questions.length) {
    return (
      <div className="bg-white rounded-3xl border border-stone-200 p-10 text-center">
        <div className="text-6xl mb-4">🎧</div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'tr' ? 'Tebrikler!' : 'Congratulations!'}
        </h2>
        <p className="text-stone-600 mb-6">
          {lang === 'tr' ? `${score} / ${questions.length} doğru cevap` : `${score} / ${questions.length} correct answers`}
        </p>
        <button
          type="button"
          onClick={onExit}
          className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
          data-testid="audio-quiz-exit"
        >
          {lang === 'tr' ? 'Tamamla' : 'Done'}
        </button>
      </div>
    );
  }

  const q = questions[round];
  const handleAnswer = (opt) => {
    const correct = opt.ar === q.correct.ar;
    setFeedback(correct ? 'correct' : 'wrong');
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      setFeedback(null);
      setRound((r) => r + 1);
    }, 1000);
  };

  return (
    <div className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
          {lang === 'tr' ? 'Soru' : 'Question'} {round + 1} / {questions.length}
        </div>
        <div className="text-xs font-semibold text-amber-700">
          {lang === 'tr' ? 'Skor' : 'Score'}: {score}
        </div>
      </div>
      <p className="text-center text-sm text-stone-600 mb-6">
        {lang === 'tr' ? 'Sesi dinle ve hangi harf olduğunu seç' : 'Listen and pick the correct letter'}
      </p>
      <div className="flex justify-center mb-6">
        <button
          type="button"
          onClick={playCurrent}
          disabled={playing}
          data-testid="audio-quiz-play"
          className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl shadow-lg transition-all ${playing ? 'bg-amber-300 scale-110' : 'bg-amber-500 hover:bg-amber-600 hover:scale-105'} text-white`}
        >
          {playing ? '🔉' : '🔊'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        {q.opts.map((opt) => (
          <button
            key={opt.ar}
            type="button"
            onClick={() => handleAnswer(opt)}
            disabled={feedback !== null}
            data-testid={`audio-quiz-option-${opt.ar}`}
            className={`p-5 rounded-xl border-2 font-semibold transition-all text-3xl ${
              feedback && opt.ar === q.correct.ar ? 'bg-green-100 border-green-500 text-green-800' :
              feedback === 'wrong' ? 'bg-stone-100 border-stone-300 text-stone-500' :
              'bg-white border-stone-200 hover:border-amber-400 text-stone-800'
            }`}
            style={{ fontFamily: 'Amiri, serif' }}
          >
            {opt.ar}
            <div className="text-xs font-normal text-stone-500 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {lang === 'en' ? opt.name_en : opt.name_tr}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============= MATCH GAME (Drag & Drop pair matching) =============
const MatchGame = ({ onExit }) => {
  const { lang } = useLang();
  const ROUND_SIZE = 6;
  const [pool] = useState(() => [...LETTER_QUIZ].sort(() => Math.random() - 0.5).slice(0, ROUND_SIZE));
  const [shuffledNames] = useState(() => [...pool].sort(() => Math.random() - 0.5));
  const [matches, setMatches] = useState({}); // ar -> name
  const [dragName, setDragName] = useState(null);
  const [dropTargetAr, setDropTargetAr] = useState(null);
  const [wrongPair, setWrongPair] = useState(null);

  React.useEffect(() => {
    if (!wrongPair) return;
    const t = setTimeout(() => setWrongPair(null), 700);
    return () => clearTimeout(t);
  }, [wrongPair]);

  const handleDrop = (ar) => {
    setDropTargetAr(null);
    if (!dragName || matches[ar]) return;
    const usedNames = Object.values(matches);
    if (usedNames.includes(dragName)) return;
    const correctName = pool.find((p) => p.ar === ar)?.name_tr;
    if (dragName === correctName) {
      setMatches({ ...matches, [ar]: dragName });
    } else {
      setWrongPair({ ar, name: dragName });
    }
    setDragName(null);
  };

  const allMatched = Object.keys(matches).length === pool.length;

  if (allMatched) {
    return (
      <div className="bg-white rounded-3xl border border-stone-200 p-10 text-center" data-testid="match-game-success">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lang === 'tr' ? 'Harika! Tüm harfleri eşleştirdin!' : 'Awesome! All matched!'}
        </h2>
        <p className="text-stone-600 mb-6">
          {lang === 'tr' ? `${ROUND_SIZE}/${ROUND_SIZE} doğru eşleştirme` : `${ROUND_SIZE}/${ROUND_SIZE} matched`}
        </p>
        <button type="button" onClick={onExit} className="px-6 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600" data-testid="match-game-exit">
          {lang === 'tr' ? 'Tamamla' : 'Done'}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-stone-200 p-6 sm:p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
          {lang === 'tr' ? 'Türkçe okunuşu → Arapça harfin üstüne sürükle' : 'Drag Turkish reading → onto its Arabic letter'}
        </div>
        <div className="text-xs font-semibold text-amber-700">
          {lang === 'tr' ? 'Eşleşti' : 'Matched'}: {Object.keys(matches).length}/{ROUND_SIZE}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {/* Arabic letters — drop targets */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{lang === 'tr' ? 'Arapça (Buraya Bırak)' : 'Arabic (Drop Here)'}</p>
          {pool.map((p) => {
            const matched = !!matches[p.ar];
            const isTarget = dropTargetAr === p.ar;
            const wrong = wrongPair?.ar === p.ar;
            return (
              <div
                key={p.ar}
                onDragOver={(e) => { e.preventDefault(); setDropTargetAr(p.ar); }}
                onDragLeave={() => setDropTargetAr(null)}
                onDrop={(e) => { e.preventDefault(); handleDrop(p.ar); }}
                data-testid={`match-drop-${p.ar}`}
                className={`relative w-full p-4 rounded-xl border-2 text-3xl text-center transition-all ${
                  matched ? 'bg-green-100 border-green-400 text-green-800' :
                  wrong ? 'bg-red-100 border-red-400 animate-shake' :
                  isTarget ? 'bg-amber-100 border-amber-500 text-amber-900 border-dashed' :
                  'bg-white border-stone-200 text-stone-900'
                }`}
                style={{ fontFamily: 'Amiri, serif' }}
              >
                {p.ar}
                {matched && (
                  <div className="text-sm mt-1 font-sans font-semibold text-green-700">
                    ✓ {matches[p.ar]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Turkish names — draggables */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{lang === 'tr' ? 'Türkçe Okunuş (Sürükle)' : 'Turkish (Drag)'}</p>
          {shuffledNames.map((n) => {
            const usedNames = Object.values(matches);
            const matched = usedNames.includes(n.name_tr);
            const dragging = dragName === n.name_tr;
            return (
              <div
                key={n.name_tr}
                draggable={!matched}
                onDragStart={() => !matched && setDragName(n.name_tr)}
                onDragEnd={() => { setDragName(null); setDropTargetAr(null); }}
                data-testid={`match-drag-${n.name_tr}`}
                className={`select-none w-full p-4 rounded-xl border-2 text-lg font-semibold text-center transition-all ${
                  matched ? 'bg-green-50 border-green-200 text-green-700 opacity-40' :
                  dragging ? 'bg-amber-200 border-amber-500 text-amber-900 cursor-grabbing scale-105' :
                  'bg-white border-stone-200 text-stone-800 cursor-grab hover:border-amber-300'
                }`}
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {n.name_tr}
              </div>
            );
          })}
        </div>
      </div>
      <button type="button" onClick={onExit} className="mt-6 text-sm text-stone-500 hover:text-stone-800" data-testid="match-game-back">
        ← {lang === 'tr' ? 'Oyunlara dön' : 'Back to games'}
      </button>
    </div>
  );
};

// ============= ONLINE ARKADAŞLAR =============
export const OnlineFriendsFull = () => {
  const { isKidsMode } = useMode();
  const { lang } = useLang();
  const [user, setUser] = useState(null);

  useEffect(() => { authAPI.getMe().then((r) => setUser(r.data)).catch(() => {}); }, []);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Online Arkadaşlar' : 'Online Friends'}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <PageHeader
          eyebrow={lang === 'tr' ? 'Çocuk Modu' : 'Kids Mode'}
          title={lang === 'tr' ? 'Online Arkadaşlar' : 'Online Friends'}
          intro={lang === 'tr' ? 'Arkadaşlarınla yarış, motivasyonun artsın.' : 'Compete with friends, boost your motivation.'}
        />
        <div className="bg-amber-50/60 border border-amber-200 rounded-2xl p-6 text-center">
          <div className="text-5xl mb-3">👥</div>
          <h2 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Yapım Aşamasında' : 'In Development'}
          </h2>
          <p className="text-stone-600 max-w-md mx-auto">
            {lang === 'tr'
              ? 'Çok yakında: Arkadaş ekleme, karşılıklı yarışlar, ilerleme paylaşma, grup hatim.'
              : 'Coming soon: Add friends, head-to-head challenges, share progress, group hatim.'}
          </p>
        </div>
      </div>
    </AppShell>
  );
};

// ============= YEARLY EVENTS (re-use religious days) =============
export const YearlyEventsFull = ReligiousDaysFull;
